import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  SortableCard,
  SortableRoot,
  AnyChatForm,
  AddMessageElement,
} from '@anyx/feature-crm/shared';

import { LineMessageFormSubView, useLineMessageStore } from '../../store';
import { LineMessageFormFieldsProps, ButtonTypeBlockFieldErrors } from '../../types';
import { DEFAULT_BUTTON_TYPE_BLOCK_CARD } from '../../utils';

import { ButtonTypeBlockCard } from './ButtonTypeBlockCard';

const MAX_BUTTON_TYPE_CARDS = 12;

const ButtonTypeBlockCards = ({
  view: { blockIndex, name: blockName },
}: {
  view: LineMessageFormSubView;
}) => {
  const { t } = useTranslation('crm');
  const navigateToBlockView = useLineMessageStore((store) => store.navigateToBlockView);
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);

  const { control, formState } = useFormContext<LineMessageFormFieldsProps>();
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: `messages.${blockIndex}.cards` as const,
  });

  const cardsId = useMemo(() => {
    return fields.map(({ id }) => id);
  }, [fields]);

  const cardsErrors = useMemo(() => {
    const error = formState.errors.messages?.[blockIndex] as ButtonTypeBlockFieldErrors;

    if (error) {
      return 'cards' in error ? error.cards : undefined;
    }

    return undefined;
  }, [formState, blockIndex]);

  return (
    <AnyChatForm.Block>
      <SortableRoot className="space-y-2" itemsId={cardsId} onMove={move}>
        {fields.map((field, fieldIndex) => {
          return (
            <SortableCard
              key={field.id}
              id={field.id}
              render={t('crm.lineMessageForm.card')}
              disabled={isFormDisabled}
              disableRemove={fields.length <= 1}
              hideRemove={isFormDisabled}
              error={!!cardsErrors?.[fieldIndex]}
              deleteTooltip={t('crm.lineMessageForm.actions.deleteCard')}
              onClick={() => {
                navigateToBlockView(blockName, blockIndex, fieldIndex);
              }}
              onRemove={() => {
                remove(fieldIndex);
              }}
            />
          );
        })}
      </SortableRoot>

      {!isFormDisabled && (
        <AddMessageElement
          currentLength={fields.length}
          maxLength={MAX_BUTTON_TYPE_CARDS}
          onAppend={() => {
            append(DEFAULT_BUTTON_TYPE_BLOCK_CARD);
          }}
          label={t('crm.lineMessageForm.actions.addCard')}
        />
      )}
    </AnyChatForm.Block>
  );
};

export function ButtonTypeBlock({ view }: { view: LineMessageFormSubView }) {
  const { cardIndex, blockIndex } = view;

  if (cardIndex !== undefined) {
    return <ButtonTypeBlockCard blockIndex={blockIndex} cardIndex={cardIndex} />;
  }

  return <ButtonTypeBlockCards view={view} />;
}
